import { calculateProposalBudget, normalizeBudgetRates, BUDGET_FX_UNAVAILABLE } from '@/lib/proposalBudget';
import React, { useMemo, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';
import type { BudgetSummaryProps, VenueFormData, BudgetSettings, VenueBudgetComputed, ExchangeRates } from './types';
import { CURRENCY_INFO, type SupportedCurrency } from './types';

export function BudgetSummary({
  budget,
  currentSettings,
  venueFormData,
  eventCurrency,
  exchangeRates,
  onSelectVenue,
}: BudgetSummaryProps) {
  // Use event currency for display
  const displayCurrency = (eventCurrency || 'USD') as SupportedCurrency;
  const currencyInfo = CURRENCY_INFO[displayCurrency] ?? CURRENCY_INFO.USD;

  const formatCurrency = useCallback((amount: number) => {
    if (!Number.isFinite(amount)) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: displayCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, [displayCurrency]);

  // Shared calculator includes unsaved form edits as well as saved budgets.
  const dynamicSummary = useMemo(() => {
    return budget.venues.map((venue) => {
      const formData = venueFormData[venue.shortlist_id];
      if (formData) {
        const totals = calculateProposalBudget({
          breakdown: { ...formData, otherCosts: { ...formData.otherCosts, venue_currency: formData.venueCurrency } },
          settings: currentSettings, attendees: budget.attendees, nights: budget.nights,
          eventCurrency: displayCurrency, exchangeRates: normalizeBudgetRates(exchangeRates),
        });
        return {
          shortlist_id: venue.shortlist_id,
          venue_name: venue.venue_name,
          venue_currency: totals.venueCurrency,
          per_attendee: Math.round(totals.perAttendeeCost),
          total_cost: Math.round(totals.totalProgramCost),
        };
      }
      // Server totals omit several categories; never present them as a complete budget.

      return {
        shortlist_id: venue.shortlist_id,
        venue_name: venue.venue_name,
        venue_currency: venue.venue_currency || 'USD',
        per_attendee: NaN,
        total_cost: NaN,
      };
    });
  }, [budget.venues, budget.summary, budget.attendees, budget.nights, venueFormData, currentSettings, exchangeRates, displayCurrency]);

  // Sort venues by per-attendee cost (cheapest first)
  const sortedSummary = [...dynamicSummary].sort((a, b) => (Number.isFinite(a.per_attendee) ? a.per_attendee : Infinity) - (Number.isFinite(b.per_attendee) ? b.per_attendee : Infinity));

  return (
    <div className="space-y-6">
      {dynamicSummary.some(v => !Number.isFinite(v.total_cost)) && <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{BUDGET_FX_UNAVAILABLE} A complete budget breakdown is also required.</p>}
      {/* Summary Header */}
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900">Budget Comparison</h2>
        <p className="text-sm text-gray-500 mt-1">
          Compare costs across {budget.venues.length} venue{budget.venues.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Summary Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-4 px-4 font-semibold text-gray-700">Venue</th>
              <th className="text-right py-4 px-4 font-semibold text-gray-700">Cost Per Attendee</th>
              <th className="text-right py-4 px-4 font-semibold text-gray-700">Total Program Cost</th>
              <th className="py-4 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {sortedSummary.map((venue, index) => {
              const isLowest = index === 0 && Number.isFinite(venue.total_cost);
              return (
                <tr
                  key={venue.shortlist_id}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{venue.venue_name}</span>
                      {isLowest && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          Lowest
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(venue.per_attendee)}
                    </span>
                    <span className="text-gray-500 text-sm ml-1">/person</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="text-lg font-semibold text-teal-600">
                      {formatCurrency(venue.total_cost)}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <button
                      onClick={() => onSelectVenue(venue.shortlist_id)}
                      className="inline-flex items-center text-sm text-teal-600 hover:text-teal-700 font-medium"
                    >
                      View Details
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500 mb-1">Attendees</p>
          <p className="text-2xl font-bold text-gray-900">{budget.attendees}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500 mb-1">Event Duration</p>
          <p className="text-2xl font-bold text-gray-900">{budget.nights} nights</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500 mb-1">Event Currency</p>
          <p className="text-2xl font-bold text-gray-900">{displayCurrency}</p>
        </div>
      </div>

      {/* Settings Summary - uses currentSettings to show live form values */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Budget Settings Applied</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-blue-700">Contingency:</span>{' '}
            <span className="font-medium text-blue-900">{currentSettings.contingency_fixed != null ? 'Fixed' : `${currentSettings.contingency_percent}%`}</span>
          </div>
          <div>
            <span className="text-blue-700">Planning Overhead:</span>{' '}
            <span className="font-medium text-blue-900">
              {currentSettings.planning_overhead_fixed !== null
                ? formatCurrency(currentSettings.planning_overhead_fixed / 100)
                : `${currentSettings.planning_overhead_percent}%`}
            </span>
          </div>
          <div>
            <span className="text-blue-700">Profit Margin:</span>{' '}
            <span className="font-medium text-blue-900">
              {currentSettings.profit_margin_fixed !== null
                ? formatCurrency(currentSettings.profit_margin_fixed / 100)
                : `${currentSettings.profit_margin_percent}%`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BudgetSummary;
